import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { api, getUserId, LEVEL_LABELS } from '@/lib/api';
import GateModal from '@/components/GateModal';
import { Code, Zap, Trophy, Lock } from 'lucide-react';
import { toast } from 'sonner';

const LEVEL_ICONS = {
  anfaenger: <Code className="w-5 h-5" />,
  fortgeschritten: <Zap className="w-5 h-5" />,
  experte: <Trophy className="w-5 h-5" />,
};

const LEVEL_COLORS = {
  anfaenger: 'from-[#10B981] to-[#059669]',
  fortgeschritten: 'from-[#3B82F6] to-[#2563EB]',
  experte: 'from-[#8B5CF6] to-[#7C3AED]',
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [gateOpen, setGateOpen] = useState(false);
  const [gateData, setGateData] = useState(null);
  const [gateTarget, setGateTarget] = useState('');
  const [loading, setLoading] = useState(null);
  const [unlockStatus, setUnlockStatus] = useState({});

  // Check unlock status for all levels on mount
  useEffect(() => {
    const userId = getUserId();
    const checkLevels = async () => {
      try {
        const [fort, exp] = await Promise.all([
          api.get('/unlock/check', { params: { targetLevel: 'fortgeschritten', userId } }),
          api.get('/unlock/check', { params: { targetLevel: 'experte', userId } }),
        ]);
        setUnlockStatus({
          anfaenger: true,
          fortgeschritten: fort.data.unlocked,
          experte: exp.data.unlocked,
        });
      } catch {}
    };
    checkLevels();
  }, []);

  const handleStart = async (level) => {
    setLoading(level);
    try {
      const userId = getUserId();
      const res = await api.get('/unlock/check', { params: { targetLevel: level, userId } });
      if (res.data.unlocked) {
        navigate('/quiz/intro', { state: { level } });
      } else {
        setGateData(res.data);
        setGateTarget(level);
        setGateOpen(true);
      }
    } catch (e) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(null);
    }
  };

  const handleStartPrev = () => {
    setGateOpen(false);
    if (gateData?.prevLevel) {
      navigate('/quiz/intro', { state: { level: gateData.prevLevel } });
    }
  };

  const isLocked = (level) => unlockStatus[level] === false;

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-4 py-4 sm:py-0" data-testid="landing-page">
      <div className="animate-fade-up max-w-lg w-full text-center">
        <h1 className="font-outfit font-extrabold text-3xl sm:text-5xl lg:text-6xl text-white mb-2 sm:mb-4 tracking-tight">
          Teste dein<br /><span className="gradient-text-brand">IT-Wissen</span>
        </h1>
        <p className="text-[#9CA3AF] text-sm sm:text-base mb-6 sm:mb-10 max-w-sm mx-auto">
          Am Ende bekommst du Score + Ranking.<br />Bewerbung optional.
        </p>

        <div className="flex flex-col gap-3 sm:gap-4 w-full max-w-xs mx-auto">
          {['anfaenger', 'fortgeschritten', 'experte'].map((level) => {
            const locked = isLocked(level);
            return (
              <Button
                key={level}
                onClick={() => handleStart(level)}
                data-testid={`start-${level}-btn`}
                className={`w-full h-12 sm:h-14 bg-gradient-to-r ${LEVEL_COLORS[level]} text-white font-outfit font-bold text-base sm:text-lg rounded-xl transition-all duration-200 shadow-lg relative
                  ${locked ? 'opacity-60 hover:opacity-70' : 'hover:opacity-90 hover:-translate-y-0.5'}
                `}
              >
                <span className="mr-2">{locked ? <Lock className="w-5 h-5" /> : LEVEL_ICONS[level]}</span>
                {LEVEL_LABELS[level]} starten
                {locked && (
                  <span className="ml-2 text-[10px] font-mono bg-black/30 px-2 py-0.5 rounded-full uppercase tracking-wide">
                    Gesperrt
                  </span>
                )}
                {loading === level && <span className="ml-2 animate-spin">...</span>}
              </Button>
            );
          })}
        </div>

        <div className="mt-6 sm:mt-12 text-xs text-[#4B5563] flex gap-4 justify-center">
          <a href="https://datenschutzportal.de/juunit/" target="_blank" rel="noopener noreferrer" className="hover:text-[#9CA3AF] transition-colors" data-testid="footer-datenschutz">
            Datenschutz
          </a>
          <span>|</span>
          <a href="https://www.juunit.com/impressum" target="_blank" rel="noopener noreferrer" className="hover:text-[#9CA3AF] transition-colors" data-testid="footer-impressum">
            Impressum
          </a>
        </div>
      </div>

      <GateModal
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        unlockData={gateData}
        targetLevel={gateTarget}
        onStartPrev={handleStartPrev}
      />
    </div>
  );
}
